import { useCallback, useMemo } from 'react';
import {
  CustomerServiceTag,
  QuoteApiModel,
  QuoteOptionApiModel,
  QuoteProviderAccountApiModel
} from '~/lib/api/smartsend/types';
import { P2GBox, P2GContainer } from '@parcel2go/core-ui';
import { SmartSendProvider } from '~/lib/consts/providers';
import ProviderName from '../../../../lib/helpers/ProviderName';
import Loading from '../../../../../../components/loading/Loading';
import { useLocalStorage } from '../../../../hooks/useLocalStorage';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { EventSections } from '../../../../lib/events/EventLogger';
import {
  CurrentSelectionType,
  DefaultExcludedServiceTags,
  ToSmartSendAnalyticsPatchModel
} from '../utils/CurrentSelectionType';
import { FilterDialog } from '../FilterDialog';
import { QuoteTagType, QuoteTagTypeOptions } from '../utils/QuoteTagType';
import FilterQuotes, { QuoteFilter } from '../utils/FilterQuotes';
import { onlyUnique } from '../../../../lib/helpers/ArrayHelper';
import GetServiceTypes from '../utils/GetServiceTypes';
import { QuoteLayoutTaskbar } from './QuoteLayoutTaskbar';
import { P2GImageMessage } from '~/components/imagemessage';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useOnLoad from '~/routes/use-on-load';
import { QuoteLayoutDisplay } from './QuoteLayoutDisplay';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { ApplierOption } from '~/apps/smartsend/lib/helpers/ApplierOption';

interface BaseViewProps {
  onSave?: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  quotes: QuoteApiModel[];
  currentSelection: CurrentSelectionType;
  estimatedValue?: number;
  quoteTagType: QuoteTagType;
  updateServiceTags: (add: CustomerServiceTag[], remove: CustomerServiceTag[]) => void;
  serviceTags: CustomerServiceTag[];

  selectedServiceProvider: QuoteProviderAccountApiModel;
  toggleSpecificTag: (tag: CustomerServiceTag) => void;
  isXs: boolean;
  children?: React.ReactNode;
}

interface ViewProps extends BaseViewProps {
  editMode: boolean;
  includeServices: (quotes: QuoteApiModel[]) => void;
  excludeServices: (quotes: QuoteApiModel[]) => void;
  wrapper: (content: React.ReactNode) => React.ReactNode;
}

interface QuoteLayoutProps extends BaseViewProps {
  onUpdate?: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  onBackClick: () => void;
  loggingSections: string[];
  setQuoteTagType: (showAll: QuoteTagType) => void;
  multiAccount: boolean;
  onSwitchAccount: () => void;
}

const View = ({
  editMode,
  quotes,
  isXs,
  toggleSpecificTag,
  updateServiceTags,
  onSave,
  includeServices,
  excludeServices,
  currentSelection,
  quoteTagType,
  serviceTags,
  children,
  estimatedValue,
  selectedServiceProvider,
  wrapper
}: ViewProps) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return wrapper(
    <>
      {editMode && <FilterDialog />}
      {quotes.length === 0 ? (
        <P2GImageMessage
          message={t('NO_RESULTS_FALLBACK', { defaultValue: 'No Results' })}
          image="/theme/common/notifications/no-results.png"
          contentKey="image-smartsend-quotes-empty-list"
        />
      ) : (
        <QuoteLayoutDisplay
          isXs={isXs}
          toggleSpecificTag={toggleSpecificTag}
          updateServiceTags={updateServiceTags}
          onSave={onSave}
          includeServices={includeServices}
          excludeServices={excludeServices}
          currentSelection={currentSelection}
          editMode={editMode}
          quoteTagType={quoteTagType}
          quotes={quotes}
          serviceTags={serviceTags}
          children={children}
          estimatedValue={estimatedValue}
          selectedServiceProvider={selectedServiceProvider}
        />
      )}
    </>
  );
};

export const QuoteLayout: React.FC<QuoteLayoutProps> = ({
  quoteTagType,
  quotes,
  setQuoteTagType,
  onSave,
  onUpdate,
  loggingSections,
  selectedServiceProvider,
  currentSelection,
  estimatedValue,
  updateServiceTags,
  serviceTags,
  multiAccount,
  toggleSpecificTag,
  isXs,
  onSwitchAccount,
  children
}: QuoteLayoutProps) => {
  const { track } = useSmartSendAnalytics();

  const editMode = quoteTagType === 'Unfiltered';

  const onTabChangeCallback = useCallback(
    (subCat?: string) => {
      track({
        type: 'quote_tab_changed',
        payload: GenerateBaseAnalyticsPayload([...loggingSections, EventSections.Quotes], {
          surface: 'quotes',
          action: 'quote-tab-change',
          tab: (subCat || 'all').toLowerCase()
        })
      });
    },
    [loggingSections, track]
  );

  useOnLoad(() => {
    track({
      type: 'smartsend_quotes_loaded',
      payload: GenerateBaseAnalyticsPayload([...loggingSections, EventSections.Quotes])
    });
  });

  useOnLoad(() => onTabChangeCallback());

  const [filter, setFilter] = useLocalStorage<QuoteFilter>(
    `SS-QUOTE-RESULTS-FILTER:V3_${selectedServiceProvider.Provider}`,
    {
      ServiceType: 'All',
      SubCategory: '',
      Search: '',
      Tags: [],
      MatchAllTags: false,
      Product: ''
    }
  );

  const onIncludeServicesCallback = useCallback(
    (quotes: QuoteApiModel[]) => {
      updateServiceTags(
        [],
        quotes.map((x) => {
          return {
            ServiceSlug: x.Service.Id,
            Tags: DefaultExcludedServiceTags,
            GroupIdentifier: SmartSendProvider
          };
        })
      );
    },
    [updateServiceTags]
  );

  const onExcludeServicesCallback = useCallback(
    (quotes: QuoteApiModel[]) =>
      updateServiceTags(
        quotes.map((x) => {
          return {
            ServiceSlug: x.Service.Id,
            Tags: DefaultExcludedServiceTags,
            GroupIdentifier: SmartSendProvider
          };
        }),
        []
      ),
    [updateServiceTags]
  );

  const categoriesMemo = useMemo(() => {
    if (selectedServiceProvider.Provider !== SmartSendProvider) {
      return [{ Name: 'All Services', Id: '' }];
    }

    if (SettingsCache.settings.Features.includes('BusinessQuotes')) {
      return [
        { Name: 'Business', Id: `${SmartSendProvider}-BUSINESS` },
        { Name: ProviderName(SmartSendProvider), Id: SmartSendProvider },
        { Name: 'All Services', Id: '' }
      ];
    } else {
      return [{ Name: 'All Services', Id: '' }];
    }
  }, [selectedServiceProvider]);

  const quotesMemo = useMemo(() => FilterQuotes(quotes, filter, quoteTagType), [filter, quotes, quoteTagType]);

  const serviceTypesMemo = useMemo(
    () => GetServiceTypes(selectedServiceProvider?.Provider?.toUpperCase(), SettingsCache.profile),
    [selectedServiceProvider]
  );

  const productNamesMemo = useMemo(
    () =>
      !quotes
        ? []
        : [
            { value: '', text: 'All' },
            ...quotes
              .map((x) => x.Service?.CourierProductName)
              .filter(onlyUnique)
              .filter((x) => !!x)
              .map((x) => ({ value: x, text: x }))
          ],
    [quotes]
  );

  const onSaveCallback = useCallback(
    (quote: QuoteApiModel, option: QuoteOptionApiModel) => {
      const senderCountryIsoCode = currentSelection?.sender?.CountryIsoCode;
      const recipientCountryIsoCode = currentSelection?.recipient?.CountryIsoCode;
      const addedExtras = option?.Extras?.map((extra) => extra.Id);

      const actions: ApplierOption[] = ['Service', 'Extras'];
      const patchModel = ToSmartSendAnalyticsPatchModel(
        {
          Service: {
            Id: quote.Service?.Id
          },
          Extras: addedExtras
        },
        actions
      );

      track({
        type: 'service_selected',
        payload: GenerateBaseAnalyticsPayload([...loggingSections, EventSections.Quotes], {
          surface: 'quotes',
          action: 'select-service',
          serviceId: quote.Service?.Id,
          serviceName: quote.Service?.Name,
          serviceProvider: quote.Service?.Provider,
          serviceAccountId: quote.Service?.AccountId,
          courierProductName: quote.Service?.CourierProductName,
          senderCountryIsoCode: senderCountryIsoCode,
          recipientCountryIsoCode: recipientCountryIsoCode,
          extraIds: option?.Extras?.map((extra) => extra.Id) ?? [],
          originalItems: currentSelection?.items,
          patchModel: patchModel
        })
      });

      onSave?.(quote, option);
      onUpdate?.(quote, option);
    },
    [loggingSections, onSave, onUpdate, currentSelection, track]
  );

  const onIncludeAllServicesCallback = useCallback(
    () => onIncludeServicesCallback(quotesMemo),
    [quotesMemo, onIncludeServicesCallback]
  );

  const onExcludeAllServicesCallback = useCallback(
    () => onExcludeServicesCallback(quotesMemo),
    [quotesMemo, onExcludeServicesCallback]
  );

  const onSetTagsCallback = useCallback(
    (tags: string[]) => setFilter((filter) => ({ ...filter, Tags: tags })),
    [setFilter]
  );

  const onSetMatchAllCallback = useCallback(
    (val: boolean) => setFilter((filter) => ({ ...filter, MatchAllTags: val })),
    [setFilter]
  );

  const onToggleFilterSubCategoryCallback = useCallback(
    (x: QuoteTagType) => {
      if (QuoteTagTypeOptions.includes(x)) {
        setQuoteTagType(x);
      }
      onTabChangeCallback(x);
      setFilter((filter) => ({ ...filter, SubCategory: x }));
    },
    [setFilter, onTabChangeCallback, setQuoteTagType]
  );

  const onSearchCallback = useCallback(
    (v: string) => {
      if (v) {
        track({
          type: 'smartsend_quotes_filter_changed',
          payload: GenerateBaseAnalyticsPayload([...loggingSections, EventSections.Quotes], {
            filter: {
              Search: v
            }
          })
        });
      }
      setFilter((filter) => ({ ...filter, Search: v }));
    },
    [loggingSections, track, setFilter]
  );

  const onSetServiceTypeCallback = useCallback(
    (v: string) => setFilter({ ...filter, ServiceType: v as any }),
    [filter, setFilter]
  );

  if (!filter) {
    return <Loading />;
  }

  return (
    <>
      <QuoteLayoutTaskbar
        categories={categoriesMemo}
        isXs={isXs}
        filter={filter}
        toggleFilterSubCategory={onToggleFilterSubCategoryCallback}
        provider={selectedServiceProvider.Provider}
        setSearch={onSearchCallback}
        serviceTypes={serviceTypesMemo}
        setServiceType={onSetServiceTypeCallback}
        productNames={productNamesMemo}
        setFilter={setFilter}
        setMatchAll={onSetMatchAllCallback}
        setTags={onSetTagsCallback}
        setQuoteTagType={setQuoteTagType}
        quoteTagType={quoteTagType}
        includeAllServices={onIncludeAllServicesCallback}
        excludeAllServices={onExcludeAllServicesCallback}
        onAccountSwitch={onSwitchAccount}
        multiAccount={multiAccount}
      />
      <P2GBox>
        <View
          isXs={isXs}
          toggleSpecificTag={toggleSpecificTag}
          updateServiceTags={updateServiceTags}
          onSave={onSaveCallback}
          includeServices={onIncludeServicesCallback}
          excludeServices={onExcludeServicesCallback}
          currentSelection={currentSelection}
          editMode={editMode}
          quoteTagType={quoteTagType}
          quotes={quotesMemo}
          serviceTags={serviceTags}
          children={children}
          estimatedValue={estimatedValue}
          selectedServiceProvider={selectedServiceProvider}
          wrapper={(x) => <>{isXs ? x : <P2GContainer maxWidth="lg">{x}</P2GContainer>}</>}
        />
      </P2GBox>
    </>
  );
};
